import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { User as FirebaseUser, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth';
import { ref, set, get, update, push, query, orderByChild, equalTo } from 'firebase/database';
import { auth, database } from './firebaseConfig';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebaseConfig';
import { FriendRequest, Friend, UserProfile } from './types';

interface GameData {
  date: string;
  winner: string;
  players: Array<{
    name: string;
    score: number;
    isFinalThrow: boolean;
    hasWon: boolean;
    throws: number;
    dice: Array<{
      value: number;
      isFrozen: boolean;
    }>;
    history?: Array<{
      diceValues: number[];
      turnScore: number;
      isFrozen?: boolean[];
    }>;
  }>;
}

interface UserStats {
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number; // New: track losses explicitly
  highestScore: number;
  totalPointsScored: number; // New: cumulative points
  averageScore: number; // New: average score per game
  averageThrowsPerGame: number; // New: efficiency metric
  tripleFreezeThrows: number;
  lostAllPointsThrows: number;
  triplaTriplatThrows: number;
  triplatThrows: number;
  averageThrowsTo90: number;
  breaks90Points: number;
  winAfterBreaking90: number;
  currentWinStreak: number; // New: current streak
  longestWinStreak: number; // New: best streak
  currentLossStreak: number; // New: current loss streak
  longestLossStreak: number; // New: worst streak
  fastestWin: number; // New: fewest throws to win
  highestSingleTurnScore: number; // New: best single turn
  overallAccuracy: number; // New: moved from accuracy data
  victoryDiceDistribution: {
    twoDice: number;
    threeDice: number;
  };
  // ── Achievement counters (June 2026) — optional so existing user records and
  //    all the initializer blocks stay valid; default to 0 wherever read. They
  //    accrue from completed casual games (same path as gamesPlayed). ──
  perfectAccuracyGames?: number; // games finished at 100% accuracy (every turn scored)
  totalFreezes?: number;         // cumulative dice locked into the freeze zone
  gamesWith3Triplats?: number;   // games where the player landed 3+ triplats
  winsUnder30?: number;          // wins achieved in fewer than 30 throws
  totalScoringTurns?: number;    // cumulative completed turns (for per-round/throw averages)
  diceInDistribution?: {         // how many dice landed in, per turn
    zero: number;
    one: number;
    two: number;
    three: number;
  };
}

interface AboutContent {
  story: string;
  youtubeEmbedCode: string;
}

export interface Place {
  id: string;
  name: string;
  lat: number;
  lng: number;
  address: string;
  iconUrl?: string;
}
interface User {
  uid: string;
  name: string;
  email: string;
  role: string;
  stats: UserStats;
  friends?: string[];
  friendRequests?: string[];
  isOnline?: boolean;
  lastActive?: number;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUserGameData: (gameData: GameData) => Promise<void>;
  updateSpecialThrowStats: (eventType: keyof UserStats) => Promise<void>;
  logPlayerAccuracyData: (diceValues: number[]) => Promise<void>;
  fixBrokenStats: () => Promise<void>;
  postEvent: (eventData: { title: string; description: string; date: string; imageFile?: File; imageUrl?: string; maxParticipants?: number }) => Promise<void>;  // Ensure postEvent type matches the actual function
  joinEvent: (eventId: string) => Promise<void>;
  aboutContent: AboutContent | null;
  fetchAboutContent: () => Promise<void>; // Add declaration
  updateAboutContent: (content: AboutContent) => Promise<void>; // Add declaration
  places: Place[];
  addPlace: (place: Omit<Place, 'id'>) => Promise<void>;
  updatePlace: (place: Place) => Promise<void>;
  deletePlace: (placeId: string) => Promise<void>;
  // Friend system functions
  sendFriendRequest: (toUserId: string, toUserName: string) => Promise<void>;
  acceptFriendRequest: (requestId: string) => Promise<void>;
  rejectFriendRequest: (requestId: string) => Promise<void>;
  removeFriend: (friendId: string) => Promise<void>;
  getFriendRequests: () => Promise<FriendRequest[]>;
  getFriends: () => Promise<Friend[]>;
  searchUsers: (searchTerm: string) => Promise<UserProfile[]>;
  isAdmin: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  login: async () => {},
  signup: async () => {},
  logout: async () => {},
  updateUserGameData: async () => {},
  updateSpecialThrowStats: async () => {},
  logPlayerAccuracyData: async () => {},
  fixBrokenStats: async () => {},  
  postEvent: async () => {}, 
  joinEvent: async () => {},
  aboutContent: null,
  fetchAboutContent: async () => {},
  updateAboutContent: async () => {},
  places: [],
  addPlace: async () => {},
  updatePlace: async () => {},
  deletePlace: async () => {},
  sendFriendRequest: async () => {},
  acceptFriendRequest: async () => {},
  rejectFriendRequest: async () => {},
  removeFriend: async () => {},
  getFriendRequests: async () => [],
  getFriends: async () => [],
  searchUsers: async () => [],
  isAdmin: false,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [aboutContent, setAboutContent] = useState<AboutContent | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [places, setPlaces] = useState<Place[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const isAuthenticated = !!user;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userData = await fetchUserData(firebaseUser);
        setUser(userData);
        setIsAdmin(userData.role === 'admin');
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // useEffect to fetch about content on mount
  useEffect(() => {
    console.log('useEffect in AuthProvider for fetching about content is running'); // Added log
    fetchAboutContent();
  }, []); // Empty dependency array ensures this runs only on mount

  useEffect(() => {
    fetchPlaces();
  }, []);


  const fetchAboutContent = async () => {
    console.log('Fetching about content...');
    try {
      const aboutRef = ref(database, 'about/content');
      const snapshot = await get(aboutRef);
      if (snapshot.exists()) {
        setAboutContent(snapshot.val());
      } else {
        // Initialize with default content if it doesn't exist
        const defaultContent: AboutContent = {
          story: 'Default About Story',
          youtubeEmbedCode: '',
        };
        console.log('About content not found, initializing with default:', defaultContent); // Added log
        console.log('Setting aboutContent state');
        setAboutContent(defaultContent);
      }
    } catch (error) {
      console.error('Error fetching about content:', error);
    }
  };

  const fetchUserData = async (firebaseUser: FirebaseUser): Promise<User> => {
    const userRef = ref(database, `users/${firebaseUser.uid}`);
    const snapshot = await get(userRef);
  
    if (snapshot.exists()) {
      const data = snapshot.val();
      const stats = data.stats || {};
      
      // Fix broken stats for existing users
      const gamesPlayed = stats.gamesPlayed || 0;
      const gamesWon = stats.gamesWon || 0;
      const gamesLost = gamesPlayed - gamesWon; // Calculate losses properly
      
      // Calculate proper averages
      const totalPointsScored = stats.totalPointsScored || 0;
      const averageScore = gamesPlayed > 0 ? Math.round(totalPointsScored / gamesPlayed) : 0;
      
      // Calculate average throws per game (if it's 0, estimate from other data)
      let averageThrowsPerGame = stats.averageThrowsPerGame || 0;
      if (averageThrowsPerGame === 0 && gamesPlayed > 0) {
        // Estimate based on average throws to 90 and typical game length
        const estimatedThrows = stats.averageThrowsTo90 || 6;
        averageThrowsPerGame = Math.round(estimatedThrows * 1.2); // Assume games are ~20% longer than throws to 90
      }
      
      // Fix overall accuracy if it's unreasonably low
      let overallAccuracy = stats.overallAccuracy || 0;
      if (overallAccuracy < 10 && gamesPlayed > 0) {
        // Estimate accuracy based on typical player performance
        overallAccuracy = 60; // Default to 60% for existing users
      }
      
      return {
        uid: firebaseUser.uid,
        name: data.name,
        email: data.email,
        role: data.role || 'user',
        stats: {
          gamesPlayed,
          gamesWon,
          gamesLost,
          highestScore: stats.highestScore || 0,
          totalPointsScored,
          averageScore,
          averageThrowsPerGame,
          tripleFreezeThrows: stats.tripleFreezeThrows || 0,
          lostAllPointsThrows: stats.lostAllPointsThrows || 0,
          triplaTriplatThrows: stats.triplaTriplatThrows || 0,
          triplatThrows: stats.triplatThrows || 0,
          averageThrowsTo90: stats.averageThrowsTo90 || 0,
          breaks90Points: stats.breaks90Points || 0,
          winAfterBreaking90: stats.winAfterBreaking90 || 0,
          currentWinStreak: stats.currentWinStreak || 0,
          longestWinStreak: stats.longestWinStreak || 0,
          currentLossStreak: stats.currentLossStreak || 0,
          longestLossStreak: stats.longestLossStreak || 0,
          fastestWin: stats.fastestWin || 0,
          highestSingleTurnScore: stats.highestSingleTurnScore || 0,
          overallAccuracy,
          victoryDiceDistribution: {
            twoDice: stats.victoryDiceDistribution?.twoDice || 0,
            threeDice: stats.victoryDiceDistribution?.threeDice || 0,
          },
          // Achievement counters — must be read back on login or badges reset to 0
          perfectAccuracyGames: stats.perfectAccuracyGames || 0,
          totalFreezes: stats.totalFreezes || 0,
          gamesWith3Triplats: stats.gamesWith3Triplats || 0,
          winsUnder30: stats.winsUnder30 || 0,
          totalScoringTurns: stats.totalScoringTurns || 0,
          diceInDistribution: {
            zero: stats.diceInDistribution?.zero || 0,
            one: stats.diceInDistribution?.one || 0,
            two: stats.diceInDistribution?.two || 0,
            three: stats.diceInDistribution?.three || 0,
          },
        },
      };
    } else {
      // Initialize new user with default stats
      const newUser: User = {
        uid: firebaseUser.uid,
        name: firebaseUser.displayName || 'Anonymous',
        email: firebaseUser.email || '',
        role: 'user',
        stats: {
          gamesPlayed: 0,
          gamesWon: 0,
          gamesLost: 0,
          highestScore: 0,
          totalPointsScored: 0,
          averageScore: 0,
          averageThrowsPerGame: 0,
          tripleFreezeThrows: 0,
          lostAllPointsThrows: 0,
          triplaTriplatThrows: 0,
          triplatThrows: 0,
          averageThrowsTo90: 0,
          breaks90Points: 0,
          winAfterBreaking90: 0,
          currentWinStreak: 0,
          longestWinStreak: 0,
          currentLossStreak: 0,
          longestLossStreak: 0,
          fastestWin: 0,
          highestSingleTurnScore: 0,
          overallAccuracy: 0,
          victoryDiceDistribution: {
            twoDice: 0,
            threeDice: 0,
          },
        },
      };
      await set(userRef, newUser);
      return newUser;
    }
  };
  

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signup = async (name: string, email: string, password: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, { displayName: name });
      const userRef = ref(database, `users/${userCredential.user.uid}`);
      const newUser: User = {
        uid: userCredential.user.uid,
        name,
        email,
        role: 'user',  // Default role for new users
        stats: {
          gamesPlayed: 0,
          gamesWon: 0,
          gamesLost: 0,
          highestScore: 0,
          totalPointsScored: 0,
          averageScore: 0,
          averageThrowsPerGame: 0,
          tripleFreezeThrows: 0,
          lostAllPointsThrows: 0,
          triplaTriplatThrows: 0,
          triplatThrows: 0,
          averageThrowsTo90: 0,
          breaks90Points: 0,
          winAfterBreaking90: 0,
          currentWinStreak: 0,
          longestWinStreak: 0,
          currentLossStreak: 0,
          longestLossStreak: 0,
          fastestWin: 0,
          highestSingleTurnScore: 0,
          overallAccuracy: 0,
          victoryDiceDistribution: {
            twoDice: 0,
            threeDice: 0,
          },
        },
      };
      await set(userRef, newUser);
      setUser(newUser);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  // Helper function to calculate accuracy from dice values (per-turn rule)
  // Hit if ANY die > 0 → 100, Miss if ALL dice = 0 → 0
  const calculateAccuracy = (diceValues: number[]): number => {
    const isTurnHit = diceValues.some((value) => value > 0);
    return isTurnHit ? 100 : 0;
  };

  // Helper function to update streaks
  const updateStreaks = (currentStats: UserStats, didWin: boolean): { currentWinStreak: number; longestWinStreak: number; currentLossStreak: number; longestLossStreak: number } => {
    if (didWin) {
      const newWinStreak = currentStats.currentWinStreak + 1;
      return {
        currentWinStreak: newWinStreak,
        longestWinStreak: Math.max(currentStats.longestWinStreak, newWinStreak),
        currentLossStreak: 0,
        longestLossStreak: currentStats.longestLossStreak,
      };
    } else {
      const newLossStreak = currentStats.currentLossStreak + 1;
      return {
        currentWinStreak: 0,
        longestWinStreak: currentStats.longestWinStreak,
        currentLossStreak: newLossStreak,
        longestLossStreak: Math.max(currentStats.longestLossStreak, newLossStreak),
      };
    }
  };

  const updateUserGameData = async (gameData: GameData) => {
    if (user) {
      const userRef = ref(database, `users/${user.uid}/stats`);
      const updatedUser = { ...user };
  
      // Explicitly type the player from gameData
      const userInGame = gameData.players.find((p): p is GameData['players'][0] => p.name === user.name);
      
      if (!userInGame) {
        console.error('User not found in game data');
        return;
      }

      // Basic game stats
      updatedUser.stats.gamesPlayed += 1;
      const didUserWin = gameData.winner === user.name;
      
      if (didUserWin) {
        updatedUser.stats.gamesWon += 1;
      } else {
        updatedUser.stats.gamesLost += 1;
      }

      // Update streaks
      const streakUpdates = updateStreaks(updatedUser.stats, didUserWin);
      updatedUser.stats.currentWinStreak = streakUpdates.currentWinStreak;
      updatedUser.stats.longestWinStreak = streakUpdates.longestWinStreak;
      updatedUser.stats.currentLossStreak = streakUpdates.currentLossStreak;
      updatedUser.stats.longestLossStreak = streakUpdates.longestLossStreak;

      // Score and efficiency stats
      updatedUser.stats.totalPointsScored += userInGame.score;
      updatedUser.stats.averageScore = Math.round(updatedUser.stats.totalPointsScored / updatedUser.stats.gamesPlayed);
      
      // Calculate average throws per game more robustly
      const currentTotalThrows = (updatedUser.stats.averageThrowsPerGame || 0) * (updatedUser.stats.gamesPlayed - 1);
      const newTotalThrows = currentTotalThrows + (userInGame.throws || 0);
      updatedUser.stats.averageThrowsPerGame = Math.round(newTotalThrows / updatedUser.stats.gamesPlayed);

      // Highest score tracking
      if (userInGame.score > updatedUser.stats.highestScore) {
        updatedUser.stats.highestScore = userInGame.score;
      }

      // Fastest win tracking
      if (didUserWin && (updatedUser.stats.fastestWin === 0 || userInGame.throws < updatedUser.stats.fastestWin)) {
        updatedUser.stats.fastestWin = userInGame.throws;
      }

      // Highest single turn score tracking
      if (userInGame.history && userInGame.history.length > 0) {
        const maxTurnScore = Math.max(...userInGame.history.map((entry: any) => entry.turnScore || 0));
        if (maxTurnScore > updatedUser.stats.highestSingleTurnScore) {
          updatedUser.stats.highestSingleTurnScore = maxTurnScore;
        }
      }

      // Overall accuracy calculation
      if (userInGame.history && userInGame.history.length > 0) {
        const totalAccuracy = userInGame.history.reduce((sum: number, entry: any) => sum + calculateAccuracy(entry.diceValues || []), 0);
        const gameAccuracy = Math.round(totalAccuracy / userInGame.history.length);
        
        // Debug logging
        console.log('Game accuracy calculation:', {
          historyLength: userInGame.history.length,
          totalAccuracy,
          gameAccuracy,
          currentOverallAccuracy: updatedUser.stats.overallAccuracy,
          gamesPlayed: updatedUser.stats.gamesPlayed
        });
        
        // Update overall accuracy (weighted average)
        const totalGames = updatedUser.stats.gamesPlayed;
        const newOverallAccuracy = parseFloat(
          ((updatedUser.stats.overallAccuracy * (totalGames - 1) + gameAccuracy) / totalGames).toFixed(2)
        );
        
        console.log('New overall accuracy:', newOverallAccuracy);
        updatedUser.stats.overallAccuracy = newOverallAccuracy;
      }

      // Handle winning logic
      if (didUserWin) {
        // Check if the player broke 90 points before winning
        if (userInGame.score >= 90) {
          updatedUser.stats.winAfterBreaking90 += 1;
        }
  
        // Update victoryDiceDistribution based on the winning dice values
        if (userInGame.dice) {
          const diceCount = userInGame.dice.filter(die => die.value > 0).length;
          if (diceCount === 2) {
            updatedUser.stats.victoryDiceDistribution.twoDice += 1;
          } else if (diceCount === 3) {
            updatedUser.stats.victoryDiceDistribution.threeDice += 1;
          }
        }
      }
  
      // Update 90+ points stats
      if (userInGame.score >= 90) {
        updatedUser.stats.breaks90Points += 1;

        // Calculate average throws to 90
        const throwsTo90 = userInGame.throws || 0;
        updatedUser.stats.averageThrowsTo90 =
          (updatedUser.stats.averageThrowsTo90 * (updatedUser.stats.breaks90Points - 1) + throwsTo90) /
          updatedUser.stats.breaks90Points;
      }

      // ── Achievement counters (additive; mirror the exact triplat/freeze
      //    formulas the match-stats path uses so badges match the game's own
      //    definitions). All derived from this game's history — no game logic
      //    is touched. ──
      const hist = userInGame.history || [];
      if (hist.length > 0) {
        // Dice-in distribution + scoring-turn count (powers avg dice-in/throw,
        // avg points/round, and the 0/1/2/3-dice-in breakdown on the profile)
        const diceIn = { zero: 0, one: 0, two: 0, three: 0 };
        let turns = 0;
        hist.forEach((e) => {
          if (!Array.isArray(e.diceValues)) return;
          turns++;
          const n = e.diceValues.filter((v) => v > 0).length;
          if (n <= 0) diceIn.zero++;
          else if (n === 1) diceIn.one++;
          else if (n === 2) diceIn.two++;
          else diceIn.three++;
        });
        updatedUser.stats.totalScoringTurns = (updatedUser.stats.totalScoringTurns || 0) + turns;
        const prevDist = updatedUser.stats.diceInDistribution || { zero: 0, one: 0, two: 0, three: 0 };
        updatedUser.stats.diceInDistribution = {
          zero: (prevDist.zero || 0) + diceIn.zero,
          one: (prevDist.one || 0) + diceIn.one,
          two: (prevDist.two || 0) + diceIn.two,
          three: (prevDist.three || 0) + diceIn.three,
        };

        // KEEP IT HUNDRED — every turn scored at least one die (100% accuracy)
        const perfectGame = hist.every(
          (e) => Array.isArray(e.diceValues) && e.diceValues.some((v) => v > 0)
        );
        if (perfectGame) {
          updatedUser.stats.perfectAccuracyGames = (updatedUser.stats.perfectAccuracyGames || 0) + 1;
        }

        // CORNERBOY/GIRL — every die locked into the freeze zone this game
        const freezes = hist.reduce(
          (count, e) => count + (Array.isArray(e.isFrozen) ? e.isFrozen.filter(Boolean).length : 0),
          0
        );
        if (freezes > 0) {
          updatedUser.stats.totalFreezes = (updatedUser.stats.totalFreezes || 0) + freezes;
        }

        // ABOVE AVERAGE — a game with 3+ triplats (all dice score, none frozen,
        // excluding triple-of-a-kind), matching the match-stats triplat rule.
        const triplats = hist.filter((e) => {
          if (!Array.isArray(e.diceValues)) return false;
          const allPositive = e.diceValues.every((v) => v > 0);
          const noFrozen = Array.isArray(e.isFrozen) ? e.isFrozen.every((f) => !f) : true;
          const uniqueVals = Array.from(new Set(e.diceValues));
          const isTripleTriple = uniqueVals.length === 1 && uniqueVals[0] >= 2 && uniqueVals[0] <= 6;
          return allPositive && noFrozen && !isTripleTriple;
        }).length;
        if (triplats >= 3) {
          updatedUser.stats.gamesWith3Triplats = (updatedUser.stats.gamesWith3Triplats || 0) + 1;
        }
      }

      // TIME IS MONEY — won the game in fewer than 30 throws
      if (didUserWin && (userInGame.throws || 0) > 0 && userInGame.throws < 30) {
        updatedUser.stats.winsUnder30 = (updatedUser.stats.winsUnder30 || 0) + 1;
      }

      await update(userRef, updatedUser.stats);
      setUser(updatedUser);
    }
  };

  // Function to fix broken stats for existing users
  const fixBrokenStats = async () => {
    if (user) {
      const userRef = ref(database, `users/${user.uid}/stats`);
      const updatedStats = { ...user.stats };
      
      // Fix games lost
      updatedStats.gamesLost = updatedStats.gamesPlayed - updatedStats.gamesWon;
      
      // Fix average score
      updatedStats.averageScore = updatedStats.gamesPlayed > 0 ? 
        Math.round(updatedStats.totalPointsScored / updatedStats.gamesPlayed) : 0;
      
      // Fix average throws per game if it's 0
      if (updatedStats.averageThrowsPerGame === 0 && updatedStats.gamesPlayed > 0) {
        const estimatedThrows = updatedStats.averageThrowsTo90 || 6;
        updatedStats.averageThrowsPerGame = Math.round(estimatedThrows * 1.2);
      }
      
      // Fix overall accuracy if it's unreasonably low
      if (updatedStats.overallAccuracy < 10 && updatedStats.gamesPlayed > 0) {
        updatedStats.overallAccuracy = 60; // Default to 60% for existing users
      }
      
      await update(userRef, updatedStats);
      setUser({ ...user, stats: updatedStats });
      
      console.log('Fixed broken stats:', updatedStats);
    }
  };
  
  
  

  const updateSpecialThrowStats = async (eventType: keyof UserStats) => {
    if (user) {
      const userRef = ref(database, `users/${user.uid}/stats`);
  
      const updatedStats = { ...user.stats };
  
      if (eventType === 'victoryDiceDistribution' || eventType === 'diceInDistribution') {
        console.error(`Cannot increment ${eventType} directly.`);
      } else if (typeof updatedStats[eventType] === 'number') {
        (updatedStats as any)[eventType] = ((updatedStats[eventType] as number) || 0) + 1;
      } else {
        console.error(`Invalid stat type for ${eventType}.`);
      }
  
      await update(userRef, updatedStats);
      setUser((prevUser) => (prevUser ? { ...prevUser, stats: updatedStats } : null));
    }
  };
  

  // Function to log accuracy data
  const logPlayerAccuracyData = async (diceValues: number[]) => {
    if (user) {
      const ENTRY_LIMIT = 100; // Adjust this number to your desired limit
      
      // Create the new turn data
      const newTurnData = {
        diceValues,
        hitMiss: diceValues.map((value) => value > 0),
        timestamp: new Date().toISOString(),
      };
  
      // Get reference to user's accuracy data
      const accuracyRef = ref(database, `users/${user.uid}/accuracyData`);
      
      // Get current entries
      const snapshot = await get(accuracyRef);
      const currentEntries = snapshot.val() || {};
      
      // Convert to array and sort by timestamp (newest first)
      const entriesArray = Object.entries(currentEntries)
        .sort(([, a]: [string, any], [, b]: [string, any]) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      
      // Keep only the most recent entries up to ENTRY_LIMIT - 1
      const limitedEntries = entriesArray.slice(0, ENTRY_LIMIT - 1);
      
      // Create new object with the new turn and limited old entries
      const updatedEntries = {
        [`turn${Date.now()}`]: newTurnData,
        ...Object.fromEntries(limitedEntries)
      };
  
      // Update the database with the limited dataset
      await set(accuracyRef, updatedEntries);
    }
  };

  // const logGameHistory = async (gameData: GameData) => {
  //   if (user) {
  //     const historyRef = ref(database, `users/${user.uid}/games/game${Date.now()}`);
  //     await set(historyRef, gameData);
  //   }
  // };
  

  const postEvent = async (eventData: { title: string; description: string; date: string; imageFile?: File; imageUrl?: string; maxParticipants?: number }) => {
    if (user && user.role === 'admin') {
      let imageUrl = eventData.imageUrl || '';
  
      try {
        // If an image file is provided, upload it to Firebase Storage
        if (eventData.imageFile) {
          const storageReference = storageRef(storage, `events/${Date.now()}_${eventData.imageFile.name}`);
          try {
            const snapshot = await uploadBytes(storageReference, eventData.imageFile);
            imageUrl = await getDownloadURL(snapshot.ref);
            console.log('Image uploaded successfully:', imageUrl);
          } catch (uploadError) {
            console.error('Error uploading image:', uploadError);
            throw new Error('Image upload failed');
          }
        }
  
        // Create a new event entry
        const eventRef = ref(database, `events/event${Date.now()}`); // Use a unique key for the event
  
        // Dynamically set the event data
        const payload: any = {
          title: eventData.title,
          description: eventData.description,
          date: eventData.date,
          imageUrl,  // Store the image URL in the event
        };
        // Only persist maxParticipants if it is a positive integer; otherwise omit (treated as unlimited)
        if (typeof eventData.maxParticipants === 'number' && eventData.maxParticipants > 0) {
          payload.maxParticipants = eventData.maxParticipants;
        }
        await set(eventRef, payload);
  
        alert('Event posted successfully');
      } catch (error: unknown) {
        if (error instanceof Error) {
          console.error('Error posting event:', error.message);
          alert(`Error posting event: ${error.message}`);
        } else {
          console.error('Unknown error:', error);
          alert('An unknown error occurred');
        }
      }
    } else {
      throw new Error('Unauthorized: Only admins can post events');
    }
  };

  const joinEvent = async (eventId: string) => {
    if (user) {
      try {
        // Get a reference to the event participants
        const eventRef = ref(database, `events/${eventId}/participants/${user.uid}`);
        const eventSnapshot = await get(ref(database, `events/${eventId}`));
    
        if (!eventSnapshot.exists()) {
          throw new Error('Event not found');
        }
  
        const eventData = eventSnapshot.val();
        const participantCount = eventData.participants ? Object.keys(eventData.participants).length : 0;
        const maxParticipants = eventData.maxParticipants || Infinity;
    
        // Check if the event has reached its max participant limit
        if (participantCount >= maxParticipants) {
          throw new Error('Event is full');
        }
    
        // Add the user to the participants list
        await set(eventRef, { name: user.name });
        alert('You have joined the event!');
      } catch (error) {
        console.error('Error joining event:', error);
        alert(`Error joining event: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else {
      alert('You must be logged in to join an event');
    }
  };

  const updateAboutContent = async (content: AboutContent) => {
    if (user && user.role === 'admin') {
      try {
        const aboutRef = ref(database, 'about/content');
        await update(aboutRef, content);
        alert('About content updated successfully!');
      } catch (error) {
        console.error('Error updating about content:', error);
        alert('Error updating about content.');
      }
    } else {
      alert('You do not have permission to update about content.');
    }
  };

  const fetchPlaces = async () => {
    try {
      const placesRef = ref(database, 'places');
      const snapshot = await get(placesRef);
      if (snapshot.exists()) {
        const placesData = snapshot.val();
        const placesArray: Place[] = Object.keys(placesData).map(key => ({
          id: key,
          ...placesData[key],
        }));
        setPlaces(placesArray);
      } else {
        setPlaces([]);
      }
    } catch (error) {
      console.error('Error fetching places:', error);
    }
  };

  const addPlace = async (place: Omit<Place, 'id'>) => {
    if (user && user.role === 'admin') {
      try {
        const newPlaceId = Date.now().toString(); // Simple unique ID based on timestamp
        await set(ref(database, `places/${newPlaceId}`), place);
        fetchPlaces(); // Refresh the list
      } catch (error) {
        console.error('Error adding place:', error);
        alert('Error adding place.');
      }
    } else {
      alert('You do not have permission to add places.');
    }
  };

  const updatePlace = async (place: Place) => {
    if (user && user.role === 'admin') {
      try {
        const placeRef = ref(database, `places/${place.id}`);
        await update(placeRef, place);
        fetchPlaces(); // Refresh the list
      } catch (error) {
        console.error('Error updating place:', error);
        alert('Error updating place.');
      }
    } else {
      alert('You do not have permission to update places.');
    }
  };

  const deletePlace = async (placeId: string) => {
    if (user && user.role === 'admin') {
      try {
        const placeRef = ref(database, `places/${placeId}`);
        await set(placeRef, null); // Set to null to remove the data
        fetchPlaces(); // Refresh the list
        alert('Place deleted successfully!');
      } catch (error) {
        console.error('Error deleting place:', error);
        alert('Error deleting place.');
      }
    } else {
      alert('You do not have permission to delete places.');
    }
  };

  // Friend system functions
  const sendFriendRequest = async (toUserId: string, toUserName: string) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      // Check if request already exists
      const existingRequestRef = query(
        ref(database, 'friendRequests'),
        orderByChild('fromUserId'),
        equalTo(user.uid)
      );
      const existingSnapshot = await get(existingRequestRef);
      
      if (existingSnapshot.exists()) {
        const requests = existingSnapshot.val();
        const alreadyRequested = Object.values(requests).some((req: any) => 
          req.toUserId === toUserId && req.status === 'pending'
        );
        if (alreadyRequested) {
          throw new Error('Friend request already sent');
        }
      }

      // Create new friend request
      const requestRef = push(ref(database, 'friendRequests'));
      const newRequest: FriendRequest = {
        id: requestRef.key!,
        fromUserId: user.uid,
        fromUserName: user.name,
        toUserId,
        toUserName,
        status: 'pending',
        timestamp: Date.now(),
      };
      
      await set(requestRef, newRequest);
      
    } catch (error) {
      console.error('Error sending friend request:', error);
      throw error;
    }
  };

  const acceptFriendRequest = async (requestId: string) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      // Get the friend request
      const requestRef = ref(database, `friendRequests/${requestId}`);
      const requestSnapshot = await get(requestRef);
      
      if (!requestSnapshot.exists()) {
        throw new Error('Friend request not found');
      }
      
      const request: FriendRequest = requestSnapshot.val();
      
      if (request.toUserId !== user.uid) {
        throw new Error('Not authorized to accept this request');
      }
      
      // Update request status
      await update(requestRef, { status: 'accepted' });
      
      // Add each user to the other's friends list
      const userFriendsRef = ref(database, `users/${user.uid}/friends`);
      const friendFriendsRef = ref(database, `users/${request.fromUserId}/friends`);
      
      // Add friend to user's friends list
      const userFriendsSnapshot = await get(userFriendsRef);
      const userFriends = userFriendsSnapshot.exists() ? userFriendsSnapshot.val() : {};
      userFriends[request.fromUserId] = true;
      await set(userFriendsRef, userFriends);
      
      // Add user to friend's friends list
      const friendFriendsSnapshot = await get(friendFriendsRef);
      const friendFriends = friendFriendsSnapshot.exists() ? friendFriendsSnapshot.val() : {};
      friendFriends[user.uid] = true;
      await set(friendFriendsRef, friendFriends);
      
      // Note: We don't need to manually manage the friendRequests node in users
      // The getFriendRequests function queries the global friendRequests node
      
    } catch (error) {
      console.error('Error accepting friend request:', error);
      throw error;
    }
  };

  const rejectFriendRequest = async (requestId: string) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      // Get the friend request
      const requestRef = ref(database, `friendRequests/${requestId}`);
      const requestSnapshot = await get(requestRef);
      
      if (!requestSnapshot.exists()) {
        throw new Error('Friend request not found');
      }
      
      const request: FriendRequest = requestSnapshot.val();
      
      if (request.toUserId !== user.uid) {
        throw new Error('Not authorized to reject this request');
      }
      
      // Update request status
      await update(requestRef, { status: 'rejected' });
      
      // Note: We don't need to manually manage the friendRequests node in users
      // The getFriendRequests function queries the global friendRequests node
      
    } catch (error) {
      console.error('Error rejecting friend request:', error);
      throw error;
    }
  };

  const removeFriend = async (friendId: string) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      // Remove friend from user's friends list
      const userFriendsRef = ref(database, `users/${user.uid}/friends`);
      const userFriendsSnapshot = await get(userFriendsRef);
      if (userFriendsSnapshot.exists()) {
        const userFriends = userFriendsSnapshot.val();
        delete userFriends[friendId];
        await set(userFriendsRef, userFriends);
      }
      
      // Remove user from friend's friends list
      const friendFriendsRef = ref(database, `users/${friendId}/friends`);
      const friendFriendsSnapshot = await get(friendFriendsRef);
      if (friendFriendsSnapshot.exists()) {
        const friendFriends = friendFriendsSnapshot.val();
        delete friendFriends[user.uid];
        await set(friendFriendsRef, friendFriends);
      }
      
    } catch (error) {
      console.error('Error removing friend:', error);
      throw error;
    }
  };

  const getFriendRequests = async (): Promise<FriendRequest[]> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      const requestsRef = query(
        ref(database, 'friendRequests'),
        orderByChild('toUserId'),
        equalTo(user.uid)
      );
      const snapshot = await get(requestsRef);
      
      if (snapshot.exists()) {
        return Object.values(snapshot.val()).filter((req: any) => req.status === 'pending') as FriendRequest[];
      }
      
      return [];
    } catch (error) {
      console.error('Error getting friend requests:', error);
      throw error;
    }
  };

  const getFriends = async (): Promise<Friend[]> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      const userFriendsRef = ref(database, `users/${user.uid}/friends`);
      const userFriendsSnapshot = await get(userFriendsRef);
      
      if (!userFriendsSnapshot.exists()) {
        return [];
      }
      
      const friendIds = Object.keys(userFriendsSnapshot.val());
      const friends: Friend[] = [];
      
      for (const friendId of friendIds) {
        const friendRef = ref(database, `users/${friendId}`);
        const friendSnapshot = await get(friendRef);
        
        if (friendSnapshot.exists()) {
          const friendData = friendSnapshot.val();
          friends.push({
            userId: friendId,
            userName: friendData.name,
            email: friendData.email,
            stats: friendData.stats || {},
            friendshipDate: Date.now(), // You might want to store this when friendship is created
            lastActive: friendData.lastActive || Date.now(),
          });
        }
      }
      
      return friends;
    } catch (error) {
      console.error('Error getting friends:', error);
      throw error;
    }
  };

  const searchUsers = async (searchTerm: string): Promise<UserProfile[]> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (!snapshot.exists()) {
        return [];
      }
      
      const users = snapshot.val();
      const searchResults: UserProfile[] = [];
      
      Object.keys(users).forEach(userId => {
        const userData = users[userId];
        if (userId !== user.uid && // Don't include current user
            (userData.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             userData.email.toLowerCase().includes(searchTerm.toLowerCase()))) {
          searchResults.push({
            uid: userId,
            name: userData.name,
            email: userData.email,
            stats: userData.stats || {},
            friends: userData.friends ? Object.keys(userData.friends) : [],
            friendRequests: userData.friendRequests ? Object.keys(userData.friendRequests) : [],
            isOnline: userData.isOnline || false,
            lastActive: userData.lastActive || Date.now(),
          });
        }
      });
      
      return searchResults;
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        signup,
        logout,
        updateUserGameData,
        updateSpecialThrowStats,
        logPlayerAccuracyData,
        fixBrokenStats,
        postEvent,
        joinEvent,
        aboutContent,
        fetchAboutContent,
        updateAboutContent,
        places,
        addPlace,
        updatePlace,
        deletePlace,
        sendFriendRequest,
        acceptFriendRequest,
        rejectFriendRequest,
        removeFriend,
        getFriendRequests,
        getFriends,
        searchUsers,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
